import React, { useCallback, useEffect, useMemo, useState } from "react";

import { LoadingView } from "../../components/common/loader";
import { ErrorMessages } from "../../components/error/errorMessages";
import SearchBox from "../../components/common/searchBox";
import { useClinicCall } from "../../components/clinic-calling/ClinicCallProvider";
import { fetchTodayAppointmentDeliveries } from "../../services/apis/dispense/todayAppointmentDeliveries";
import { useAuthSelector } from "../../services/selectors/authSelector";

import * as Common from "../../components/common";
import * as Icons from "../../assets/svg/icons";

const getApiErrorMessage = (error) => {
	const data = error?.response?.data;
	const validationError = data?.data?.error;
	if (validationError && typeof validationError === "object") {
		const firstValue = Object.values(validationError)[0];
		if (Array.isArray(firstValue) && firstValue[0]) return firstValue[0];
		if (typeof firstValue === "string") return firstValue;
	}

	return data?.message || error?.message || "Unable to load appointment deliveries.";
};

const formatAppointmentTime = (value) => {
	const displayValue = value?.display_time || value?.display_date_time || value?.date_and_time;
	return displayValue || "Time not set";
};

const compactList = (values) => values.filter((value) => value != null && String(value).trim() !== "");

const firstValue = (...values) => {
	const value = values.find((entry) => entry != null && String(entry).trim() !== "");
	return value == null ? "" : value;
};

const getPatientName = (item) => (
	item?.pharmacy_patient?.name ||
	compactList([item?.pharmacy_patient?.last_name, item?.pharmacy_patient?.first_name]).join(" ") ||
	"Patient"
);

const getPatientDetailsData = (item) => {
	const patient = item?.pharmacy_patient || {};
	const clinicPatient = item?.clinic_patient || {};
	const fallbackNameParts = String(patient.name || "").trim().split(/\s+/);
	const fallbackFirstName = fallbackNameParts.length > 1 ? fallbackNameParts.slice(1).join(" ") : "";
	const fallbackLastName = fallbackNameParts[0] || getPatientName(item);

	return {
		...patient,
		first_name: firstValue(patient.first_name, clinicPatient.first_name, fallbackFirstName),
		last_name: firstValue(patient.last_name, clinicPatient.last_name, fallbackLastName),
		gender: firstValue(patient.gender, clinicPatient.gender, patient.sex, clinicPatient.sex),
		dob: firstValue(patient.dob, clinicPatient.dob, patient.date_of_birth, clinicPatient.date_of_birth),
		age: firstValue(patient.age, clinicPatient.age),
		phn: firstValue(patient.phn, clinicPatient.phn, patient.health_card_number, clinicPatient.health_card_number),
	};
};

const getPatientDisplayName = (patient) => (
	compactList([patient?.last_name, patient?.first_name]).join(", ") ||
	getPatientName({ pharmacy_patient: patient })
).toUpperCase();

const getPatientMeta = (patient) => {
	const gender = String(patient?.gender || "").trim();
	const age = String(patient?.age || "").trim();
	const dob = String(patient?.dob || "").trim();
	const ageOrDob = age ? `${age} yrs` : dob;

	return compactList([gender, ageOrDob]).join(", ");
};

const getClinicName = (item, appointment) => (
	appointment?.clinic?.name ||
	item?.clinic_contact?.clinic_name ||
	"Clinic"
);

const getDoctorLabel = (appointment) => (
	appointment?.doctor_display_name ||
	appointment?.doctor?.name ||
	appointment?.doctor?.full_name ||
	appointment?.doctor_name ||
	appointment?.provider?.name ||
	appointment?.provider_name ||
	(appointment?.doctor_id ? `Doctor #${appointment.doctor_id}` : "")
);

const getClinicPhone = (item, appointment) => (
	item?.clinic_contact?.clinic_phone ||
	appointment?.clinic?.phone ||
	null
);

const getVisibleAppointment = (item, selectedClinic) => {
	const appointments = Array.isArray(item?.appointments) ? item.appointments : [];
	if (!selectedClinic) return appointments[0] || null;

	return appointments.find((appointment) => getClinicName(item, appointment) === selectedClinic) || null;
};

const normalizeManualPhone = (value) => String(value || "").trim().replace(/[^+\d]/g, "");

const TodayAppointmentDeliveries = () => {
	const { pharmacyId } = useAuthSelector();
	const { call, isCallActive, startClinicCall } = useClinicCall();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [payload, setPayload] = useState(null);
	const [manualCallTarget, setManualCallTarget] = useState(null);
	const [manualPhone, setManualPhone] = useState("");
	const [manualPhoneError, setManualPhoneError] = useState("");
	const [selectedClinic, setSelectedClinic] = useState("");

	const patients = useMemo(() => {
		if (Array.isArray(payload?.patients)) return payload.patients;
		return [];
	}, [payload]);

	const clinicFilterOptions = useMemo(() => {
		const clinicNames = patients
			.flatMap((item) => {
				const appointments = Array.isArray(item?.appointments) ? item.appointments : [];
				const appointmentClinicNames = appointments.map((appointment) => getClinicName(item, appointment));
				return appointmentClinicNames.length > 0 ? appointmentClinicNames : [getClinicName(item, null)];
			})
			.map((clinicName) => String(clinicName || "").trim())
			.filter((clinicName) => clinicName && clinicName !== "Clinic");

		const uniqueClinicNames = Array.from(new Set(clinicNames)).sort((left, right) =>
			left.localeCompare(right, undefined, { sensitivity: "base" })
		);

		return uniqueClinicNames;
	}, [patients]);
	const showClinicFilters = clinicFilterOptions.length > 0 && patients.length > 0;

	const filteredPatients = useMemo(() => {
		if (!selectedClinic) return patients;

		return patients.filter((item) => {
			return Boolean(getVisibleAppointment(item, selectedClinic));
		});
	}, [patients, selectedClinic]);

	useEffect(() => {
		if (clinicFilterOptions.length === 0) {
			if (selectedClinic) setSelectedClinic("");
			return;
		}

		if (!selectedClinic || !clinicFilterOptions.includes(selectedClinic)) {
			setSelectedClinic(clinicFilterOptions[0]);
		}
	}, [clinicFilterOptions, selectedClinic]);

	const loadAppointmentDeliveries = useCallback(async () => {
		if (!pharmacyId) return;

		setLoading(true);
		setError("");

		try {
			const data = await fetchTodayAppointmentDeliveries(pharmacyId);
			setPayload(data);
		} catch (loadError) {
			setPayload(null);
			setError(getApiErrorMessage(loadError));
		} finally {
			setLoading(false);
		}
	}, [pharmacyId]);

	useEffect(() => {
		loadAppointmentDeliveries();
	}, [loadAppointmentDeliveries]);

	const startCallForItem = async (item, appointment, manualToPhone = "") => {
		const appointmentId = appointment?.id;
		if (!appointmentId) return;

		await startClinicCall({
			pharmacyId,
			appointmentId,
			pharmacyPatientId: item?.pharmacy_patient?.id,
			manualToPhone: manualToPhone || "",
			// Number to dial natively if the masked SIP line can't be established.
			fallbackPhone: manualToPhone || getClinicPhone(item, appointment) || "",
			clinicName: getClinicName(item, appointment),
			patientName: getPatientName(item),
		});
	};

	const handleCallClick = (item, appointment) => {
		if (!appointment?.id || isCallActive) return;

		const clinicPhone = getClinicPhone(item, appointment);
		if (clinicPhone) {
			startCallForItem(item, appointment);
			return;
		}

		setManualPhone("");
		setManualPhoneError("");
		setManualCallTarget({ item, appointment });
	};

	const closeManualPhoneModal = () => {
		setManualCallTarget(null);
		setManualPhone("");
		setManualPhoneError("");
	};

	const submitManualPhone = async () => {
		if (!manualCallTarget) return;

		const normalizedPhone = normalizeManualPhone(manualPhone);
		if (normalizedPhone.replace(/\D/g, "").length < 7) {
			setManualPhoneError("Enter a valid clinic phone number.");
			return;
		}

		const target = manualCallTarget;
		closeManualPhoneModal();
		await startCallForItem(target.item, target.appointment, normalizedPhone);
	};

	return (
		<div className="app-wrapper flex justify-center">
			<div className="app-screen no-shades w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12 h-full overflow-x-hidden">
				<div className="relative p-4">
					<Common.TopBar />

					<div className="flex gap-3 mb-3">
						<SearchBox pharmacyId={pharmacyId} onRefresh={loadAppointmentDeliveries} isRefreshing={loading} />
					</div>

					{showClinicFilters ? (
						<div className="mb-2 flex gap-2 overflow-x-auto pb-1">
							{clinicFilterOptions.map((clinicName) => (
								<button
									key={clinicName}
									type="button"
									onClick={() => setSelectedClinic(clinicName)}
								className={`shrink-0 rounded-full border-2 px-5 py-2 text-xs font-bold leading-none shadow-sm ${
									selectedClinic === clinicName
										? "border-emerald-600 bg-emerald-50 text-emerald-800"
										: "border-gray-300 bg-white text-gray-700"
								}`}
								>
									{clinicName}
								</button>
							))}
						</div>
					) : null}

					<div className="title-row my-2 flex items-center justify-between gap-2">
						<h1 className="shrink-0 whitespace-nowrap text-lg font-bold">Today's Appointment</h1>
					</div>

					{loading ? (
						<div className="flex h-96 justify-center pb-4">
							<LoadingView />
						</div>
					) : error ? (
						<div className="flex h-96 flex-col items-center justify-center gap-3 pb-4">
							<ErrorMessages error={error} />
							<button
								type="button"
								onClick={loadAppointmentDeliveries}
								className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
							>
								Retry
							</button>
						</div>
					) : patients.length === 0 ? (
						<div className="flex h-96 items-center justify-center pb-4 text-center">
							<p className="max-w-[260px] text-sm font-semibold text-slate-500">
								No appointments for today.
							</p>
						</div>
					) : filteredPatients.length === 0 ? (
						<div className="flex h-96 items-center justify-center pb-4 text-center">
							<p className="max-w-[260px] text-sm font-semibold text-slate-500">
								No appointments for selected clinic.
							</p>
						</div>
					) : (
						<div className="routes-list">
							{filteredPatients.map((item, index) => {
								const firstAppointment = getVisibleAppointment(item, selectedClinic);
								const hasClinicPhone = Boolean(getClinicPhone(item, firstAppointment));
								const doctorLabel = getDoctorLabel(firstAppointment);
								const patientDetailsData = getPatientDetailsData(item);
								const appointmentTime = formatAppointmentTime(firstAppointment);
								const patientDisplayName = getPatientDisplayName(patientDetailsData);
								const patientMeta = getPatientMeta(patientDetailsData);
								const isThisCallActive =
									isCallActive &&
									call?.appointmentId != null &&
									firstAppointment?.id != null &&
									String(call.appointmentId) === String(firstAppointment.id);
								const callDisabled = !firstAppointment?.id || (isCallActive && !isThisCallActive);

								return (
									<div
										key={`${item?.pharmacy_patient?.id || "patient"}-${firstAppointment?.id || index}`}
										className="card rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm mb-3"
										id={`patientCard-${index}`}
									>
										<div className="grid grid-cols-[76px_minmax(0,1fr)] gap-3 pb-2">
											<div className="rounded-lg border border-green-100 bg-green-50 px-2 py-2 text-center">
												<p className="text-[10px] font-bold uppercase leading-none text-green-700">Appt</p>
												<p className="mt-1 whitespace-nowrap text-xs font-bold leading-tight text-green-800">{appointmentTime}</p>
											</div>
											<div className="min-w-0 pt-1">
												<h2 className="truncate text-base font-semibold uppercase tracking-tight text-gray-900">
													{patientDisplayName}
												</h2>
												<p className="mt-0.5 text-xs uppercase text-gray-400">
													{patientMeta || "-"}
												</p>
											</div>
										</div>

										<div className="grid grid-cols-2 gap-1.5 py-1.5">
											<div className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1">
												<p className="text-[10px] uppercase tracking-wide text-gray-400">PHN</p>
												<p className="truncate text-xs text-gray-500">{patientDetailsData.phn || "-"}</p>
											</div>
											<div className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1">
												<p className="text-[10px] uppercase tracking-wide text-gray-400">Doctor</p>
												<p className="truncate text-xs text-gray-500">{doctorLabel || "-"}</p>
											</div>
										</div>

										<div className="pt-1">
											<button
												type="button"
												disabled={callDisabled}
												onClick={() => handleCallClick(item, firstAppointment)}
												className={`flex h-9 w-full items-center justify-center gap-2 rounded-md px-4 text-sm font-medium ${
													callDisabled
														? "bg-gray-300 text-gray-500"
														: "bg-[#10b981] text-white"
												}`}
											>
												<Icons.PhoneIcon color={callDisabled ? "#6b7280" : "#ffffff"} />
												{isThisCallActive ? "Calling Clinic" : hasClinicPhone ? "Call Clinic" : "Enter Clinic Phone"}
											</button>
										</div>
									</div>
								);
							})}
						</div>
					)}

					{manualCallTarget ? (
						<div
							className="fixed inset-0 z-[22000] flex items-end justify-center bg-slate-950/60 px-3 pb-3 sm:items-center sm:p-4"
							role="dialog"
							aria-modal="true"
							aria-labelledby="manual-clinic-phone-title"
						>
							<div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl">
								<h2 id="manual-clinic-phone-title" className="text-lg font-bold text-slate-950">
									Clinic phone
								</h2>
								<p className="mt-1 text-sm text-slate-500">
									Enter the clinic number for this call only.
								</p>
								<input
									type="tel"
									inputMode="tel"
									value={manualPhone}
									onChange={(event) => {
										setManualPhone(event.target.value);
										setManualPhoneError("");
									}}
									placeholder="Clinic phone number"
									className="mt-4 h-12 w-full rounded-xl border border-slate-300 px-3 text-base font-semibold text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
								/>
								{manualPhoneError ? (
									<p className="mt-2 text-sm font-semibold text-red-600">{manualPhoneError}</p>
								) : null}
								<div className="mt-4 flex gap-2">
									<button
										type="button"
										onClick={closeManualPhoneModal}
										className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700"
									>
										Cancel
									</button>
									<button
										type="button"
										onClick={submitManualPhone}
										className="h-11 flex-1 rounded-xl bg-[#10b981] text-sm font-bold text-white"
									>
										Start Call
									</button>
								</div>
							</div>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default TodayAppointmentDeliveries;
